import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CONTROLLERS, ENDPOINTS } from 'src/app/constants';
import {
  ACHToken,
  CashPayment,
  ChargeACHToken,
  ChargeCard,
  ChargeCardToken,
  ExportPaymentPlanReportRequest,
  MapPatientReq,
  Note,
  Order,
  PaymentAPIResponse,
  RefundTransactionReqCommand,
  Response,
  SendPaymentLink,
  SubscriptionInvoice,
  TerminalPayment,
  TokenizedCard,
  Transaction,
  TransactionHistoryReqCommand,
  TransactionHistoryResp,
} from 'src/app/models';
import { ApiService } from '../api.service';
import { TransactionHistoryModel, TransactionHistoryRequestCommand } from 'src/app/models/payments/transaction-history.model';
import { OrderListReqCommand, OrderListResponse } from 'src/app/models/payments/order.model';
import { CardDetailsResponse, CheckPaymnetPlanTransactionResp } from 'src/app/models/payments/transaction.model';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private api: string = CONTROLLERS.PAYMENTS.PAYMENT;
  constructor(private apiService: ApiService) { }

  addTokenizedCard(body: TokenizedCard): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.ADD_TOKENIZED_CARD}`,
      body
    );
  }

  chargeCardToken(body: ChargeCardToken): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CHARGE_CARD_TOKEN}`,
      body
    );
  }

  chargeCard(body: ChargeCard): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CHARGE_CARD}`,
      body
    );
  }

  chargeCardPayNow(body: ChargeCard): Observable<Response<PaymentAPIResponse>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CHARGE_CARD_PAY_NOW}`,
      body
    );
  }

  chargeACHToken(body: ChargeACHToken): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CHARGE_ACH_TOKEN}`,
      body
    );
  }

  getACHTransactionStatus(transactionId: number): Observable<Response<any>> {
    return this.apiService.getById(
      transactionId.toString(),
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.GET_ACH_TRANSACTION_STATUS}`
    );
  }

  cashPayment(body: CashPayment): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CASH_PAYMENT}`,
      body
    );
  }

  createACHToken(body: ACHToken): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CREATE_ACH_TOKEN}`,
      body
    );
  }

  deleteCardToken(tokenId: number): Observable<Response<any>> {
    return this.apiService.delete(
      tokenId.toString(),
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.DELETE_CARD_TOKEN}`
    );
  }

  terminalPayment(body: TerminalPayment): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.TERMINAL_PAYMENT}`,
      body
    );
  }

  sendPaymentLink(body: SendPaymentLink): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.SEND_PAYMENT_LINK}`,
      body
    );
  }

  checkPayment(formData: FormData): Observable<Response<any>> {
    return this.apiService.postMultipartFormData(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CHECK_PAYMENT}`,
      formData
    );
  }

  getPaymentNotes(
    practiceId: number,
    patientId: number
  ): Observable<Response<Note[]>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.GET_PAYMENT_NOTES}`,
      {
        practiceId,
        patientId,
      }
    );
  }

  getTransactions(
    paginationFilter: TransactionHistoryReqCommand
  ): Observable<Response<TransactionHistoryResp>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.GET_TRANSACTIONS}`,
      paginationFilter
    );
  }

  exportToExcelTransaction(data : ExportPaymentPlanReportRequest) : Observable<Blob>{
    return this.apiService.postBlob(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.EXPORT_TO_EXCEL_TRANSACTION}`,
      data
    );
  }

  refundTransaction(
    reqBody: RefundTransactionReqCommand
  ): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.REFUND_TRANSACTION}`,
      reqBody
    );
  }

  GetTransactionById(transactionId: number): Observable<Response<Transaction>> {
    return this.apiService.get(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.GET_TRANSACTION_BY_ID
      }/${transactionId.toString()}`
    );
  }

  calculateTax(
    amount: number,
    practiceId: number,
    // Optional parameters to use after payment
    isAfterPayment?: boolean,
    transactionId?: number,
    taxAmount?: number
    // address: string,
    // state: string,
    // city: string,
    // zip: string
  ): Observable<any> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CALCULATE_TAX}`,
      {
        amount,
        practiceId,
        isAfterPayment: isAfterPayment || false,
        transactionId: transactionId || 0,
        taxAmount
        // address,
        // state,
        // city,
        // zip,
      }
    );
  }

  getOrderById(practiceId: number, chainId: number): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.GET_ORDER_BY_ID}`,
      { practiceId, chainId }
    );
  }

  getOrderByOrderId(practiceId: number, chainId: number,orderId: number): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.GET_ORDER_BY_ORDER_ID}`,
      { practiceId, chainId ,orderId}
    );
  }

  updateOrder(body: Order): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.UPDATE_ORDER}`,
      body
    );
  }

  createInvoice(body: SubscriptionInvoice): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CREATE_INVOICE}`,
      body
    );
  }
  getTransactionHistory(body: TransactionHistoryRequestCommand): Observable<Response<TransactionHistoryModel>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.GET_TRANSACTION_HISTORY}`,
      body
    );
  }

  getOrderList(
    reqBody: OrderListReqCommand
  ): Observable<Response<OrderListResponse>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.GET_ORDER_LIST}`,
      reqBody
    );
  }


downloadPDF(orderNumber: string,Order: string) : Observable<Blob>{
  return this.apiService.postBlob(
    `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.DOWNLOAD_ORDER}`,
    {orderNumber, order: Order }
   );

  }
  mapPatient(req: MapPatientReq): Observable<Response<any>> {
    return this.apiService.put("",
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.MAP_PATIENT}`,
      req
    );
  }

  downloadPatientRecieptPDF(patientId: string,transactionId: string,patientReciept: string) : Observable<Blob>{
    return this.apiService.postBlob(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.PATIENT_RECIEPT_DOWNLOAD}`,
      {patientId, transactionId,patientReciept }
     );
  }
  cancelOrder(id: number): Observable<Response<OrderListResponse>> {
    return this.apiService.delete(id.toString(),`${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.INACTIVE_ORDER}`);
  }
  
  getCardDetailsByOrderId(id: number): Observable<Response<CardDetailsResponse>> {
    return this.apiService.get(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CARD_DETAILS_BY_ORDER_ID}/${id}`
    );
  }

  UpdateTransactionWbStatus(transactionId: string,writebackStatus: number): Observable<Response<boolean>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.UPDATE_TRANSACTION_WB_STATUS}`,{transactionId,writebackStatus}
    );
  }

  CheckPaymnetPlanTransaction(transactionId: number): Observable<Response<CheckPaymnetPlanTransactionResp>> {
    return this.apiService.get(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT.CHECK_PAYMENT_PLAN_TRANSCTION}/${transactionId}`
    );
  }
}



